import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

type ToastType = 'success' | 'danger' | 'warning' | 'info';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastOptions {
  // When true the toast stays until the user dismisses it (or acts on it).
  // Used for things like the "new version available" prompt.
  persistent?: boolean;
  // Optional action button rendered inside the toast.
  action?: ToastAction;
  // Override the default 4s auto-dismiss (ignored when persistent).
  durationMs?: number;
}

interface Toast {
  id: number;
  message: string;
  type: ToastType;
  persistent: boolean;
  action?: ToastAction;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType, options?: ToastOptions) => number;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
  dismissToast: (id: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

let toastId = 0;

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback((message: string, type: ToastType = 'success', options?: ToastOptions) => {
    const id = ++toastId;
    const persistent = options?.persistent ?? false;
    setToasts(prev => [...prev, { id, message, type, persistent, action: options?.action }]);

    // Auto-remove after the configured duration unless the toast is persistent.
    if (!persistent) {
      setTimeout(() => removeToast(id), options?.durationMs ?? 4000);
    }
    return id;
  }, [removeToast]);

  const showSuccess = useCallback((message: string) => { showToast(message, 'success'); }, [showToast]);
  const showError = useCallback((message: string) => { showToast(message, 'danger'); }, [showToast]);
  const showWarning = useCallback((message: string) => { showToast(message, 'warning'); }, [showToast]);
  const showInfo = useCallback((message: string) => { showToast(message, 'info'); }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, showSuccess, showError, showWarning, showInfo, dismissToast: removeToast }}>
      {children}

      {/* Toast Container */}
      {toasts.length > 0 && (
        <div className="toast-container">
          {toasts.map(toast => (
            <div
              key={toast.id}
              className={`alert alert-${toast.type}`}
              role="alert"
              style={{
                backgroundColor: `var(--button-${toast.type})`,
                color: toast.type === 'warning' ? '#222' : '#fff',
                border: 'none'
              }}
            >
              <strong>{toast.message}</strong>

              {(toast.action || toast.persistent) && (
                <div className="toast-actions">
                  {toast.action && (
                    <button
                      type="button"
                      className="toast-action-btn"
                      onClick={() => {
                        toast.action?.onClick();
                        removeToast(toast.id);
                      }}
                    >
                      {toast.action.label}
                    </button>
                  )}
                  {toast.persistent && (
                    <button
                      type="button"
                      className="toast-dismiss-btn"
                      aria-label="Dismiss"
                      onClick={() => removeToast(toast.id)}
                    >
                      ×
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastContext;
